import { useEffect, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import type { MeUser } from '~/server/types';
import { useNotifications } from '~/modules/notifications';
import { useChatBoxes } from '../hooks/use-chat-boxes';
import { useConversations } from '../hooks/use-conversations';
import { useConversationCache } from '../hooks/use-conversation-cache';
import { useContactSuggestionsCache } from '../hooks/use-contact-suggestions';
import { useRealTimeChat } from '../hooks/use-real-time-chat';
import {
    useClearTypingIndicator,
    useTypingIndicatorManager,
} from '../hooks/use-typing-indicator';
import {
    DRAWER_HEADER_HEIGHT_REM,
    DRAWER_MARGIN_REM,
    playNotificationSound,
    SPACING_PX,
} from '../utils';
import { ChatInboxDrawer } from './chat-inbox/ChatInboxDrawer';
import { ChatBox } from './chat-box/ChatBox';

/**
 * The chat inbox drawer and chat boxes for the current user. This component
 * should only be rendered once in the root component. The chat inbox drawer and
 * the chat box states are managed by the `useChatBoxes` hook.
 * @see {@link useChatBoxes}
 */
export function ChatBoxes({ user }: { user: MeUser }) {
    const [professionalID, setProfessionalID] = useState<number | null>(null);
    useContactSuggestionsCache();
    useTypingIndicatorManager();
    const clearTypingIndicator = useClearTypingIndicator();
    const {
        chatInboxState,
        chatBoxStates,
        isChatBoxesDisabled,
        isChatBoxesHidden,
        isInboxOrChatBoxOpen,
        setChatInboxState,
        isChatBoxActive,
        openMinimisedChatBox,
        hideInboxAndChatBoxes,
    } = useChatBoxes();
    const {
        conversations,
        isLoading,
        hasMore,
        branchProfessionalID,
        setBranch,
        setParams,
        refetch,
        loadMore,
    } = useConversations({
        professionalID,
        limit: 20,
    });
    const { invalidateCachedConversation } = useConversationCache();
    const { notifications, refetch: refetchNotifications } = useNotifications();

    useRealTimeChat({
        onMessage: (message) => {
            if (
                message.type !== 'chat/message' &&
                message.type !== 'chat/migrate' &&
                message.type !== 'chat/update' &&
                message.type !== 'chat/read' &&
                message.type !== 'chat/removed' &&
                message.type !== 'chat/message/updated'
            ) {
                return;
            }
            refetch();
            refetchNotifications();
            const inboxContexts = [
                null,
                ...user.Professionals.filter((p) => p.HasInboxAccess).map(
                    (p) => p.ID,
                ),
            ];
            // Invalidate cached conversation avoid showing outdated messages.
            for (const professionalID of inboxContexts) {
                if (!isChatBoxActive(message.conversation_id, professionalID)) {
                    invalidateCachedConversation(
                        message.conversation_id,
                        professionalID,
                    );
                }
            }
            if (message.type === 'chat/migrate') {
                for (const professionalID of inboxContexts) {
                    if (
                        !isChatBoxActive(
                            message.original_conversation_id,
                            professionalID,
                        )
                    ) {
                        invalidateCachedConversation(
                            message.original_conversation_id,
                            professionalID,
                        );
                    }
                }
            }
            if (
                message.type === 'chat/message' ||
                message.type === 'chat/migrate'
            ) {
                const isSelfMemberSender =
                    message.sender_type === 'Member' &&
                    message.sender_id === user.ID;
                const isSelfProfessionalSender =
                    message.sender_type === 'Professional' &&
                    inboxContexts.includes(message.sender_id);
                // Play notification sound and open a minimised chat box if a
                // new message is received that wasn't sent by the current user.
                if (
                    !isSelfMemberSender &&
                    !isSelfProfessionalSender &&
                    message.sender_user_id !== user.ID &&
                    !message.is_muted
                ) {
                    const openContext = message.is_member_participant
                        ? null
                        : (message.professional_ids[0] ?? null);
                    openMinimisedChatBox(message.conversation_id, openContext);
                    if (message.is_sound_enabled) {
                        void playNotificationSound();
                    }
                }
                // Clear the typing indicator for a participant after they send a message.
                clearTypingIndicator(
                    message.conversation_id,
                    message.participant_id,
                );
            }
        },
    });

    // Show the chat messages button when there are new unread messages.
    useEffect(() => {
        if (chatInboxState === 'hidden' && notifications.totalUnreadCount) {
            setChatInboxState('closed');
        }
        // Do not include chatOpenState in the dependencies to prevent
        // re-opening when the chat open state is changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications.totalUnreadCount]);

    if (isChatBoxesDisabled || chatInboxState === 'hidden') {
        return null;
    }

    return (
        <>
            {isInboxOrChatBoxOpen && !isChatBoxesHidden && (
                <div
                    className="fixed inset-0 z-fixed bg-black/30 duration-300 animate-in fade-in"
                    onClick={() => hideInboxAndChatBoxes()}
                />
            )}
            <div
                className={cn(
                    'pointer-events-none fixed inset-x-0 bottom-0 z-fixed flex flex-row-reverse items-end justify-start',
                    // Prevent the floating button from shifting when the scrollbar is locked.
                    '[body[data-scroll-locked]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,15px))]',
                    isChatBoxesHidden && 'hidden',
                )}
                style={{
                    right: `${DRAWER_MARGIN_REM}rem`,
                    gap: `${SPACING_PX}px`,
                }}
            >
                <ChatInboxDrawer
                    className="pointer-events-auto h-[calc(100dvh-3rem)] min-h-80 shrink-0 transition-transform duration-300"
                    style={{
                        transform:
                            chatInboxState === 'closed'
                                ? `translateY(calc(100% - ${DRAWER_HEADER_HEIGHT_REM}rem))`
                                : undefined,
                    }}
                    conversations={conversations}
                    user={user}
                    professionalID={professionalID}
                    branchProfessionalID={branchProfessionalID}
                    isLoading={isLoading}
                    hasMore={hasMore}
                    onInboxChange={(nextProfessionalID) => {
                        setProfessionalID(nextProfessionalID);
                        setBranch(null);
                    }}
                    onBranchChange={setBranch}
                    onFiltersChange={({ search, projectIDs, isArchived }) => {
                        setParams({
                            search,
                            buildHubIDs: projectIDs,
                            isArchived,
                        });
                    }}
                    onLoadMore={loadMore}
                />
                {chatBoxStates.map((chatBoxState) => (
                    <ChatBox
                        key={`${chatBoxState.id}:${chatBoxState.professionalID}`}
                        className="pointer-events-auto shrink-0"
                        user={user}
                        conversationID={chatBoxState.id}
                        professionalID={chatBoxState.professionalID}
                        conversation={
                            chatBoxState.id > 0 &&
                            chatBoxState.professionalID === professionalID
                                ? conversations?.find(
                                      (c) => c.ID === String(chatBoxState.id),
                                  )
                                : undefined
                        }
                    />
                ))}
            </div>
        </>
    );
}
